import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../../msalConfig';
import { getItems, FAMILY_LISTS } from '../../api/figApi';
import MediaCover from '../components/MediaCover';

export default function Music() {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const response = await instance.acquireTokenSilent({ ...graphRequest, account });
    return response.accessToken;
  }, [instance, accounts]);

  const [items, setItems]             = useState(null); // null = loading
  const [error, setError]             = useState(null);
  const [sortBy, setSortBy]           = useState('recent');
  const [filterValue, setFilterValue] = useState('all');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const token = await getToken();
        const all = await getItems(token, FAMILY_LISTS.MEDIA_ITEMS);
        if (!cancelled) {
          setItems(all.filter(item => item.MediaType === 'Music'));
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [getToken]);

  // Distinct non-empty Composer values for the filter dropdown
  const composerOptions = items
    ? [...new Set(items.map(i => i.Composer).filter(Boolean))].sort()
    : [];

  // Build display list: filter then sort
  let displayed = items ?? [];
  if (filterValue !== 'all') {
    displayed = displayed.filter(i => i.Composer === filterValue);
  }
  displayed = [...displayed].sort((a, b) => {
    switch (sortBy) {
      case 'recent':
        return (b.Created ?? '').localeCompare(a.Created ?? '');
      case 'artist':
        return (a.Artist ?? '').toLowerCase().localeCompare((b.Artist ?? '').toLowerCase());
      case 'album':
        return (a.Album ?? '').toLowerCase().localeCompare((b.Album ?? '').toLowerCase());
      case 'composer': {
        const ac = (a.Composer ?? '').toLowerCase();
        const bc = (b.Composer ?? '').toLowerCase();
        if (!ac && bc) return 1;
        if (ac && !bc) return -1;
        return ac.localeCompare(bc);
      }
      default:
        return 0;
    }
  });

  return (
    <div className="FV-shelf">
      <h1 className="FV-shelf-title">Music</h1>

      <div className="fv-browse-toolbar">
        <select
          className="fv-browse-select"
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          aria-label="Sort by"
        >
          <option value="recent">Recently Added</option>
          <option value="artist">Artist</option>
          <option value="album">Album</option>
          <option value="composer">Composer</option>
        </select>

        <select
          className="fv-browse-select"
          value={filterValue}
          onChange={e => setFilterValue(e.target.value)}
          aria-label="Filter by composer"
        >
          <option value="all">All Composers</option>
          {composerOptions.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {items === null && !error && (
        <p className="FV-empty">Loading…</p>
      )}

      {error && (
        <p className="fv-browse-error">{error}</p>
      )}

      {items !== null && !error && displayed.length === 0 && (
        <p className="fv-browse-empty">
          {filterValue !== 'all' ? 'No music matches that filter.' : 'No music yet.'}
        </p>
      )}

      {displayed.length > 0 && (
        <div className="fv-browse-grid">
          {displayed.map(item => (
            <div
              key={item.id}
              className="fv-browse-card"
              onClick={() => navigate(`/music/${item.id}`)}
            >
              <MediaCover item={item} aspectRatio="1:1" size="md" />
              <span className="fv-browse-card-title">{item.Title}</span>
              {(item.Composer || item.Artist) && (
                <span className="fv-browse-card-secondary">
                  {item.Composer || item.Artist}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
